import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { NavItemsLoad } from 'app/app-layout/state/actions';
import { JwtName } from 'app/core/authentication/constants';
import { Credentials, Password, PasswordResetToken, Username, LoginUuidModel } from 'app/core/authentication/models';
import { AuthRestConnectorService } from 'app/core/authentication/services/auth-rest-connector.service';
import { LicenseCheckCompleted, LicenseCheckFailed, LicenseUpdate } from 'app/core/authentication/state/actions/auth-license-check.action';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { ErrorMessage } from 'app/core/error-handler/constants';
import { Error } from 'app/core/error-handler/models';
import { createErrorRaised } from 'app/core/error-handler/state/actions';
import { LoadFeatures } from 'app/core/feature-control/state/actions';
import { LoadUserFromLoggedInUser } from 'app/core/user/state/actions';
import { empty, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { LicenseRequestInterface } from '../models/license-request.model';
import { Router } from '@angular/router';

import {
  AuthActionType,
  LoadTokenCompleted,
  Login,
  LoginCompleted,
  LoginFailed,
  LoginUuid,
  LoginUuidCompleted,
  LoginUuidFailed,
  CheckLoginUuid,
  LogoutCompleted,
  PasswordReset,
  PasswordResetSent,
  PasswordResetTokenInvalid,
  PasswordResetTokenValid,
  PasswordResetTokenValidate,
  PasswordUpdate,
  PasswordUpdateCompleted,
} from './actions';

@Injectable()
export class AuthEffects {
  public source: string = 'AuthEffects';

  /**
   * Load jwt into state from localStorage
   */
  @Effect()
  public loadJwt$: Observable<Action> = this._actions$
    .pipe(
      ofType(AuthActionType.LoadToken),
      switchMap(() =>
        of(localStorage.getItem(JwtName.jwtName)).pipe(
          switchMap((jwt: string|undefined) => {
            // If the end user ever clears their local storage then the jwt stored as 'cz-jwt' can come back "undefined".
            if (!jwt || jwt === 'undefined') {
              return [new LoadTokenCompleted(null)];
            }
            return [
              new LoadTokenCompleted(jwt),
              new LoadFeatures(),
              new NavItemsLoad(jwt),
              new LoadUserFromLoggedInUser(),
            ];
          }),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        ),
      ));

  /**
   * Check that the domain is licensed
   */
  @Effect()
  public licenseCheck$: Observable<Action> = this._actions$
  .pipe(
    ofType(AuthActionType.LicenseCheck),
    switchMap(() => {
      return this._authConnector.licenseCheck().pipe(
        switchMap((status: number) => [new LicenseCheckCompleted(status)]),
        catchError(() => of(new LicenseCheckFailed(''))),
      );
    }));

    /**
   * Check that the domain is licensed
   */
  @Effect()
  public licenseUpdate$: Observable<Action> = this._actions$
  .pipe(
    ofType(AuthActionType.LicenseUpdate),
    map((action: LicenseUpdate) => action.payload),
    switchMap((licenseKeyData: LicenseRequestInterface) => {
      return this._authConnector.licenseUpdate(licenseKeyData).pipe(
        switchMap((status: number) => [new LicenseCheckCompleted(status)]),
        catchError(() => of(new LicenseCheckFailed(''))),
      );
    }));

   /**
   * Login
   */
  @Effect()
  public login$: Observable<Action> = this._actions$
    .pipe(
      ofType(AuthActionType.Login),
      map((action: Login) => action.payload),
      switchMap((creds: Credentials) => {
        return this._authConnector.login(creds).pipe(
          switchMap((jwt: string) => {
            localStorage.setItem(JwtName.jwtName, jwt);
            return [
              new LoginCompleted(jwt),
              new LoadFeatures(),
              new NavItemsLoad(jwt),
              new LoadUserFromLoggedInUser(),
            ];
          }),
          catchError((err: any) => of(new LoginFailed(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }),
    );

    @Effect()
    public loginUuid$: Observable<Action> = this._actions$
      .pipe(
        ofType(AuthActionType.LoginUuid),
        map((action: LoginUuid) => action.payload),
        switchMap((creds: LoginUuidModel) => {
          return this._authConnector.loginUuid(creds).pipe(
            switchMap((jwt: string) => {
              localStorage.setItem(JwtName.jwtName, jwt);
              return [
                new LoginCompleted(jwt),
                new LoadFeatures(),
                new NavItemsLoad(jwt),
                new LoadUserFromLoggedInUser(),
              ];
            }),
            catchError((err: any) => {
              if (err.message.includes("401 Unauthorized"))
              {
                this._router.navigate(['/sso/error']);
              }
    
              return of(new LoginFailed(new Error({
                message: ErrorMessage.unexpected,
                errorResponse: err,
                source: this.source,
              })));
            }),
          );
        }),
      );

      @Effect()
      public checkLoginUuid$: Observable<Action> = this._actions$
      .pipe(
        ofType(AuthActionType.CheckLoginUuid),
        map((action: CheckLoginUuid) => action.payload),
        switchMap((creds: LoginUuidModel) =>
          this._authConnector.loginUuid(creds).pipe(
            switchMap((jwt: string) => [
            ]),
            catchError((err: any) => {
              if (err.message.includes("401 Unauthorized")) {
                this._router.navigate(['/sso/error']);
              }
    
              return of(new LoginFailed(new Error({
                message: ErrorMessage.unexpected,
                errorResponse: err,
                source: this.source,
              })));
            }),
          )
        )
      );

    @Effect({ dispatch: false })
    public loginFailedRedirect$: Observable<Action> = this._actions$.pipe(
      ofType(AuthActionType.LoginFailed),
      switchMap(() => {
        // this._router.navigate(['/sso/error']);
        return of(null);
      })
    );

  /**
   * Logout
   */
  @Effect()
  public logout$: Observable<Action> = this._actions$
    .pipe(
      ofType(AuthActionType.Logout),
      switchMap(() => {
        return this._authConnector.logout().pipe(
            switchMap(() => {
              localStorage.removeItem(JwtName.jwtName);
              return [
                new LogoutCompleted(),
              ];
            }),
            catchError((err: any) => of(createErrorRaised(new Error({
              message: ErrorMessage.unexpected,
              errorResponse: err,
              source: this.source,
            })))),
          );
      }));

   /**
   * Logout Failed
   */
  @Effect()
  public logoutFailed$: Observable<Action> = this._actions$
    .pipe(
      ofType(AuthActionType.LogoutFailed),
      switchMap(() => {
        this._browserService.toLogin();
        return empty();
      }),
    );

  /**
   * Password Reset
   */
  @Effect()
  public passwordReset$: Observable<Action> = this._actions$
    .pipe(
      ofType(AuthActionType.PasswordReset),
      map((action: PasswordReset) => action.payload),
      switchMap((username: Username) => {
        return this._authConnector.passwordReset(username).pipe(
          switchMap((status: number) => [new PasswordResetSent(status.toString())]),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  /**
   * Validate Password Reset Token
   */
  @Effect()
  public validatePasswordResetToken$: Observable<Action> = this._actions$
    .pipe(
      ofType(AuthActionType.PasswordResetTokenValidate),
      map((action: PasswordResetTokenValidate) => action.payload),
      switchMap((resetToken: PasswordResetToken) => {
        return this._authConnector.validatePasswordResetToken(resetToken).pipe(
          switchMap((jwt: string) => [
            new PasswordResetTokenValid(jwt),
            new LoadUserFromLoggedInUser(),
          ]),
          catchError((err: any) => of(new PasswordResetTokenInvalid(err))),
        );
      }));

  /**
   * Update Password
   */
  @Effect()
  public updatePassword$: Observable<Action> = this._actions$
    .pipe(
      ofType(AuthActionType.PasswordUpdate),
      map((action: PasswordUpdate) => action.payload),
      switchMap((password: Password) => {
        return this._authConnector.updatePassword(password).pipe(
          switchMap((status: number) => [new PasswordUpdateCompleted(status.toString())]),
          catchError((err: any) => of(createErrorRaised(new Error({
            message: ErrorMessage.unexpected,
            errorResponse: err,
            source: this.source,
          })))),
        );
      }));

  constructor(private readonly _actions$: Actions,
              private readonly _authConnector: AuthRestConnectorService,
              private readonly _browserService: BrowserService,
              private readonly _router: Router
              ) {}

}
